/**
 * A page of a list endpoint. The backend's `ResponseHandler` sends a paginated result as the bare
 * records array and puts the totals in headers — `X-Total-Count` (absent when the count was
 * skipped), `X-Has-Next`, and the standard `Content-Range: items 0-19/125` (`/*` without a count).
 * Ported from portal-ui-lib's `parsePaginationHeaders`.
 */
export interface Page<T> {
  items: T[];
  /** Null when the endpoint skipped the count (`withTotal: false`). */
  total: number | null;
  hasNext: boolean;
}

function items<T>(body: unknown): T[] {
  if (Array.isArray(body)) {
    return body as T[];
  }
  if (body && typeof body === 'object') {
    const record = body as { records?: unknown; data?: unknown };
    if (Array.isArray(record.records)) {
      return record.records as T[];
    }
    if (Array.isArray(record.data)) {
      return record.data as T[];
    }
  }
  return [];
}

export function readPage<T>(body: unknown, headers: Headers): Page<T> {
  const list = items<T>(body);
  let total: number | null = null;
  const count = headers.get('X-Total-Count');
  if (count !== null && /^\d+$/.test(count)) {
    total = Number(count);
  } else {
    const range = /\/(\d+)$/.exec(headers.get('Content-Range') ?? '');
    if (range) {
      total = Number(range[1]);
    }
  }
  const hasNextHeader = headers.get('X-Has-Next');
  const hasNext =
    hasNextHeader !== null
      ? hasNextHeader === 'true'
      : /<[^>]+>;\s*rel="next"/i.test(headers.get('Link') ?? '');
  return { items: list, total, hasNext };
}
